import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/v8_hand mark 22072025.png';
import { JobSeeker, CompanyProfile } from '../services/api';

const Navbar: React.FC = () => {
  const { isAuthenticated, userType, user } = useAuth();

  const getProfilePicture = (user: JobSeeker | CompanyProfile) => {
    if (userType === 'jobseeker') {
      const jobSeeker = user as JobSeeker;
      const name = `${jobSeeker.firstName}+${jobSeeker.lastName}`;
      if (!jobSeeker.profilePictureUrl || jobSeeker.profilePictureUrl.includes('example')) {
        return `https://ui-avatars.com/api/?name=${name}&background=random&color=fff&size=150`;
      }
      return jobSeeker.profilePictureUrl;
    } else {
      const company = user as CompanyProfile;
      var name = 'N+A';
      if (company.companyName) {
        name = company.companyName.replace(/\s/g, '+');
      }
      if (!company.companyLogoUrl || company.companyLogoUrl.includes('example')) {
        return `https://ui-avatars.com/api/?name=${name}&background=random&color=fff&size=150`;
      }
      return company.companyLogoUrl;
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          
          {/* Left: Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center no-underline">
              <img 
                src={logo} 
                alt="EazyHire Logo" 
                className="h-16 w-auto" // Increased height
              />
            </Link>
          </div>

          {/* Center: Navigation Links */}
          <div className={`hidden md:flex items-center ${isAuthenticated ? 'flex-grow justify-center' : 'mx-auto'}`}>
            <div className="flex items-baseline space-x-8">
              <NavLink 
                to="/" 
                className={({ isActive }) => 
                  `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                    isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                Home
              </NavLink>
              <NavLink 
                to="/about" 
                className={({ isActive }) => 
                  `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                    isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                About Us
              </NavLink>
              <NavLink 
                to="/help" 
                className={({ isActive }) => 
                  `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                    isActive 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
              >
                Help
              </NavLink>
              {isAuthenticated && (
                <>
                  <NavLink 
                    to="/dashboard" 
                    className={({ isActive }) => 
                      `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                        isActive 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`
                    }
                  >
                    Dashboard
                  </NavLink>
                  {userType === 'jobseeker' && (
                    <>
                      <NavLink 
                        to="/jobs" 
                        className={({ isActive }) => 
                          `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                            isActive 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`
                        }
                      >
                        Browse Jobs
                      </NavLink>
                      <NavLink 
                        to="/my-applications" 
                        className={({ isActive }) => 
                          `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                            isActive 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`
                        }
                      >
                        My Applications
                      </NavLink>
                      <NavLink 
                        to="/my-feedback" 
                        className={({ isActive }) => 
                          `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                            isActive 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                          }`
                        }
                      >
                        My Feedback
                      </NavLink>
                    </>
                  )}
                  {userType === 'company' && (
                    <NavLink 
                      to="/candidates" // Assuming a /candidates route for companies
                      className={({ isActive }) => 
                        `inline-block px-3 py-2 rounded-md text-sm font-medium transition-colors no-underline ${
                          isActive 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`
                      }
                    >
                      Potential Candidates
                    </NavLink>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right: Auth Buttons */}
          <div className="hidden md:flex items-center justify-end">
            {isAuthenticated && user ? (
              <div className="flex items-center space-x-4">
                <NavLink
                  to="/profile"
                  className="block rounded-full overflow-hidden border-2 border-transparent hover:border-blue-500 transition"
                >
                  <img 
                    src={getProfilePicture(user)} 
                    alt="Profile" 
                    className="w-10 h-10 object-cover"
                  />
                </NavLink>
                <Link
                  to="/logout"
                  className="flex items-center bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition no-underline"
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>
                  Logout
                </Link>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="flex items-center bg-white text-blue-600 border border-blue-600 px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-50 transition no-underline"
                >
                  <i className="fas fa-sign-in-alt mr-2"></i>
                  Login
                </Link>
                <Link
                  to="/register"
                  className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 transition no-underline"
                >
                  <i className="fas fa-user-plus mr-2"></i>
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button (optional, for future enhancement) */}
          <div className="-mr-2 flex md:hidden">
            {/* Mobile menu button can be added here */}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;